import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, ensureCsrfCookie, xsrfHeader } from "./api";
import { bus } from "./bus";

/**
 * Customer-portal authentication. Completely separate from staff auth:
 * - Different token (portal.token)
 * - Hits /v1/tracker/portal/auth/login → portal_users table
 * - Does NOT inject X-Tenant-Id (tenant is derived server-side from the
 *   portal_users.tenant_id column)
 */

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "/api";

export interface PortalUser {
  id: number;
  name: string;
  email: string;
  tenant_id: number;
  jcontrol_client_id: number | null;
  client_name?: string | null;
  site_ids: number[];
}

interface PortalAuthState {
  loading: boolean;
  user: PortalUser | null;
}

interface PortalAuthContextValue extends PortalAuthState {
  login:  (email: string, password: string, code?: string, recoveryCode?: string) => Promise<{ twoFactorRequired?: boolean }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

// Portal auth is now an httpOnly cookie set by the backend (audit M7); the
// token is never readable by JS. We only track whether we *believe* we're
// logged in for UI bootstrapping — the cookie is the real credential.
const SESSION_HINT_KEY = "portal.session";
export const portalSessionHint = {
  get: () => localStorage.getItem(SESSION_HINT_KEY) === "1",
  set: (on: boolean) => (on ? localStorage.setItem(SESSION_HINT_KEY, "1") : localStorage.removeItem(SESSION_HINT_KEY)),
};

interface PortalRequestInit extends Omit<RequestInit, "body" | "headers"> {
  body?: unknown;
  headers?: Record<string, string>;
  anon?: boolean;
  _retry?: boolean;
}

async function portalApi<T>(path: string, init?: PortalRequestInit): Promise<T> {
  const url = path.startsWith("http") ? path : `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  // The tracker host is Sanctum-stateful, so POSTs without the XSRF header 419 (audit SUP-09).
  const headers: Record<string, string> = { Accept: "application/json", ...xsrfHeader(), ...(init?.headers ?? {}) };
  if (init?.body !== undefined && !(init.body instanceof FormData)) headers["Content-Type"] = "application/json";
  const body: BodyInit | undefined = init?.body === undefined
    ? undefined
    : init.body instanceof FormData
      ? init.body
      : JSON.stringify(init.body);
  const res = await fetch(url, {
    ...init,
    headers,
    // Send the httpOnly portal_token cookie (audit M7) instead of a bearer.
    credentials: "include",
    body,
  });
  if (res.status === 419 && !init?._retry) {
    await ensureCsrfCookie({ force: true });
    return portalApi<T>(path, { ...init, _retry: true });
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) { try { data = JSON.parse(text); } catch { data = text; } }
  if (!res.ok) {
    const body = data as { message?: string } | null;
    // Same session-expired bounce as the staff api(): on 401 from any
    // authed request, drop the cached token and emit auth:expired so
    // PortalAuthProvider's listener clears React state and RequirePortalAuth
    // bounces to /portal/login.
    if (res.status === 401 && !init?.anon) {
      portalSessionHint.set(false);
      bus.emit("portal-auth:expired", { url });
    }
    throw new ApiError(res.status, data, body?.message ?? `${res.status} ${res.statusText}`);
  }
  return data as T;
}

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PortalAuthState>({ loading: true, user: null });

  const refresh = useCallback(async () => {
    // No JS-readable token any more — just ask /me; the httpOnly cookie either
    // authenticates or we get a 401. Skip the call if we've never logged in to
    // avoid a guaranteed 401 on first visit.
    if (!portalSessionHint.get()) {
      setState({ loading: false, user: null });
      return;
    }
    try {
      const me = await portalApi<{ user: PortalUser }>("/v1/tracker/portal/auth/me");
      portalSessionHint.set(true);
      setState({ loading: false, user: me.user });
    } catch {
      portalSessionHint.set(false);
      setState({ loading: false, user: null });
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Session-expired fan-out from portalApi. Mirrors the staff
  // AuthProvider so a 401 mid-session drops the cached user and lets
  // RequirePortalAuth bounce the next render to /portal/login.
  useEffect(() => {
    return bus.on("portal-auth:expired", () => {
      setState({ loading: false, user: null });
    });
  }, []);

  const login = useCallback(async (email: string, password: string, code?: string, recoveryCode?: string) => {
    const res = await portalApi<{
      token?: string;
      user?: PortalUser;
      two_factor_required?: boolean;
    }>(
      "/v1/tracker/portal/auth/login",
      { method: "POST", body: { email, password, code, recovery_code: recoveryCode }, anon: true }
    );
    if (res.two_factor_required && !res.token) {
      return { twoFactorRequired: true };
    }
    if (res.user) {
      // The session is now an httpOnly cookie set by the backend (audit M7);
      // we only remember a non-sensitive hint that we're logged in.
      portalSessionHint.set(true);
      setState({ loading: false, user: res.user });
    }
    return { twoFactorRequired: false };
  }, []);

  const logout = useCallback(async () => {
    try { await portalApi("/v1/tracker/portal/auth/logout", { method: "POST" }); } catch {}
    portalSessionHint.set(false);
    setState({ loading: false, user: null });
  }, []);

  return (
    <PortalAuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used within PortalAuthProvider");
  return ctx;
}

/**
 * Authed fetch helpers for portal-side calls. These intentionally don't
 * route through the staff `useApi` because that hook injects the staff token
 * and X-Tenant-Id header — wrong scope for a portal user.
 */
export const portalGet  = <T,>(path: string) => portalApi<T>(path, { method: "GET" });
export const portalPost = <T,>(path: string, body?: unknown) => portalApi<T>(path, { method: "POST", body });
